import { DateTime } from 'luxon';
import { datetime, RRule, RRuleSet } from 'rrule';
import { TIME_ZONE_UTC } from '@/constants';
import type { DecoratedExperienceEvent } from '../types';

const getEventStartDate = (event: DecoratedExperienceEvent): DateTime<true> => {
  // rrule.js requires UTC dates with local times
  // See https://github.com/jakubroztocil/rrule#timezone-support
  return event.startDate.setZone(TIME_ZONE_UTC, {
    keepLocalTime: true,
  });
};

const MAX_RECURRENCE_WINDOW = 365; // days

const calcRecurrenceEnd = (event: DecoratedExperienceEvent): DateTime<true> => {
  const startDate = getEventStartDate(event);
  const definedEndDate =
    event.isAllDay && event.lastEndDate
      ? event.lastEndDate
      : event.lastEndDateTime;
  const fallbackEndDate = startDate.plus({ days: MAX_RECURRENCE_WINDOW });

  return definedEndDate || fallbackEndDate;
};

export const calcNextStartDate = (
  event: DecoratedExperienceEvent,
  displayTimezone: string
): DateTime => {
  const rruleSet = new RRuleSet();
  const options = RRule.parseString(event.rrule || '');
  const startDate = getEventStartDate(event);
  const boundaryEndDate = calcRecurrenceEnd(event);

  // dtstart defines the first instance in the recurrence set
  options.dtstart = datetime(
    startDate.year,
    startDate.month,
    startDate.day,
    startDate.hour,
    startDate.minute,
    startDate.second
  );

  if (options.until) {
    const until = DateTime.fromJSDate(boundaryEndDate.toJSDate(), {
      zone: event.isAllDay ? TIME_ZONE_UTC : displayTimezone,
    });

    options.until = datetime(
      until.year,
      until.month,
      until.day,
      until.hour,
      until.minute,
      until.second
    );
  }

  rruleSet.rrule(new RRule(options));

  const nextDate = rruleSet.after(startDate.toJSDate(), true);

  if (nextDate) {
    // The timepart in `date` is the correct local time but shifted into UTC.
    // Example: 10pm EDT (22:00) in EDT (-4) is expressed as 18:00-0400.
    // We must parse the date in UTC then convert it back to the user's current
    // zone to get the correct local time.
    // See Luxon example @ https://github.com/jakubroztocil/rrule#important-use-utc-dates
    const dateInUtc = DateTime.fromJSDate(nextDate).toUTC();
    const dateInLocal = dateInUtc.setZone(displayTimezone, {
      keepLocalTime: true,
    });
    return dateInLocal;
  }

  return boundaryEndDate;
};

export const sortByNextStartDate = (
  events: DecoratedExperienceEvent[],
  timezone: string
): DecoratedExperienceEvent[] => {
  return events.sort((a, b) => {
    const nextStartDateA = calcNextStartDate(a, timezone);
    const nextStartDateB = calcNextStartDate(b, timezone);
    if (!nextStartDateA) {
      return -1;
    }
    if (!nextStartDateB) {
      return 1;
    }

    return nextStartDateA > nextStartDateB ? 1 : -1;
  });
};
